/**
 * GsplatRelighting - Relighting effect for gaussian splats
 *
 * Applies scene lights (point, directional, spot) to gaussian splats
 * via modifySplatColor(). Supports smooth fade between original and relit colors.
 *
 * Key design:
 * - Persistent effect (does NOT self-disable like reveal)
 * - Max 16 lights, linear-space blending with gamma decode/encode
 * - Pre-allocated flat arrays for uniforms (avoid GC)
 * - Composable: exports snippet code so reveal effect can include relighting
 *   in its combined shader. Both effects run simultaneously.
 * - No int uniforms — uses zero-color sentinel to detect unused light slots
 *
 * Uses PlayCanvas 2.16+ shader API:
 * - modifySplatCenter, modifySplatRotationScale, modifySplatColor
 * - material.getShaderChunks(lang).set('gsplatModifyVS', code)
 */
import * as pc from 'playcanvas';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';
import { SPLAT_SHADOW_GLSL, SPLAT_SHADOW_WGSL } from './shadows/splatShadowShader';
import { CLOUD_SHADOW_GLSL, CLOUD_SHADOW_WGSL } from './clouds/cloudShaders';
import { ensureShadowMapBindings } from './shadows/shadowPlaceholder';

// ---- Relighting snippet (uniforms + helper function) ----
// These can be prepended to any gsplatModifyVS shader. Call applyRelighting() in modifySplatColor.
// Mirror clip snippet is prepended so all composed effects automatically include it, then the
// splat shadow receiving code and the cloud shadows (effects/clouds) it uses.

export const relightingSnippetGLSL = mirrorClipSnippetGLSL + SPLAT_SHADOW_GLSL + CLOUD_SHADOW_GLSL + /* glsl */ `
#define MAX_RELIGHT_LIGHTS 16

uniform float uRelightFade;
uniform vec3 uRelightAmbient;
uniform vec3 uRelightLightPos[MAX_RELIGHT_LIGHTS];
uniform vec3 uRelightLightColor[MAX_RELIGHT_LIGHTS];
// vec4 (range in .x) — scalar/vec2 uniform arrays force WGSL's WrappedF32/
// WrappedVec2F wrapper structs, whose prelude isn't injected into 2.20's gsplat
// compute pass (unresolved-type → invalid pipeline). vec4 is 16-byte aligned.
// .x range (0 = directional), .y directional shadow slot + 1, .z spot / point shadow slot + 1, .w clouds' sun (1)
uniform vec4 uRelightLightRange[MAX_RELIGHT_LIGHTS];
uniform vec3 uRelightLightDir[MAX_RELIGHT_LIGHTS];
uniform vec4 uRelightLightCosAngles[MAX_RELIGHT_LIGHTS]; // cosInner in .x, cosOuter in .y
uniform mat4 uRelightModelMatrix;

vec3 rl_gammaToLinear(vec3 c) { return pow(c, vec3(2.2)); }
vec3 rl_linearToGamma(vec3 c) { return pow(max(c, vec3(0.0)), vec3(1.0/2.2)); }

void applyRelighting(vec3 center, inout vec4 color) {
#ifdef SHADOW_PASS
    // Faint splats (floaters) don't cast (see uSplatCasterLimits).
    if (color.a < uSplatCasterLimits.y) color.a = 0.0;
#else
    // Shadow-only mode (relighting off, shadows on): the scan's baked light includes the light
    // that casts, so a shadow darkens the splat by the shadow strength.
    bool mapShadowOnly = uSplatShadowGlobal.y < 0.5 && (uSplatShadowParams[0].x > 0.0 || uSplatShadowParams[1].x > 0.0 || uSplatLocalAtlas.w > 0.5);
    // Cloud shadows (effects/clouds) darken the colour the same way while relighting is off (or
    // switched off by the visitor, when the map shadows, which remove relit light, stay off).
    bool cloudShadowOnly = uCloudShadowGlobal.x > 0.5 && uCloudShadowGlobal.y < 0.5;
    bool shadowOnly = mapShadowOnly || cloudShadowOnly;
    if (uRelightFade <= 0.0 && !shadowOnly) return;

    // Transform center from model-space to world-space via the entity's world matrix.
    // Unified mode: identity (centers already world-space in work buffer).
    // Non-unified mode: entity world transform (needed when swap splats have overrides).
    vec3 worldPos = (uRelightModelMatrix * vec4(center, 1.0)).xyz;
    vec3 worldNormal = normalize(mat3(uRelightModelMatrix) * rl_splatNormal);
    float shadow0 = splatShadowFactor(0, worldPos, worldNormal);
    float shadow1 = splatShadowFactor(1, worldPos, worldNormal);
    float cloudShadow = splatCloudShadowFactor(worldPos);
    if (shadowOnly) {
        // Darken the colour as it shows on screen, so a strength of 0.6 leaves 40% of the
        // brightness, about what a real shadow baked into a sunny scan looks like. (The same
        // factor in linear light took only a third off, and shadows were hard to see.)
        // spot / point shadows fade out with their light (splatLocalShadowFactor)
        float local = 1.0;
        for (int s = 0; s < 4; s++) local *= splatLocalShadowFactor(s, worldPos, worldNormal, true);
        color.rgb *= (mapShadowOnly ? shadow0 * shadow1 * local : 1.0) * cloudShadow;
        return;
    }
    vec3 linear = rl_gammaToLinear(color.rgb);

    vec3 lightSum = vec3(0.0);
    for (int i = 0; i < MAX_RELIGHT_LIGHTS; ++i) {
        vec3 lc = uRelightLightColor[i];
        if (lc.r + lc.g + lc.b <= 0.0) continue;
        float range = uRelightLightRange[i].x;
        if (range <= 0.0) {
            // Directional / hemispheric: uniform contribution, removed where the light's
            // shadow map says something blocks it (.y = shadow slot + 1, 0 = no shadow)
            float slot = uRelightLightRange[i].y;
            float visible = slot > 1.5 ? shadow1 : (slot > 0.5 ? shadow0 : 1.0);
            // .w = 1: the light the clouds take as their sun, so their shadows remove it
            if (uRelightLightRange[i].w > 0.5) visible *= cloudShadow;
            lightSum += lc * visible;
        } else {
            // Point / spot: inverse-square falloff
            float dist = length(uRelightLightPos[i] - worldPos);
            dist = max(dist, 0.5);  // Prevent singularity near light
            float atten = 1.0 / (dist * dist);
            // Soft range cutoff so light doesn't bleed infinitely
            float rangeFade = clamp(1.0 - dist / range, 0.0, 1.0);
            atten *= rangeFade;
            // Spotlight cone attenuation (point lights have dir=vec3(0), skipped)
            vec3 lightDir = uRelightLightDir[i];
            if (dot(lightDir, lightDir) > 0.0) {
                vec3 toSplat = normalize(worldPos - uRelightLightPos[i]);
                float cosAngle = dot(toSplat, lightDir);
                float cosOuter = uRelightLightCosAngles[i].y;
                float cosInner = uRelightLightCosAngles[i].x;
                float spotFade = clamp((cosAngle - cosOuter) / max(cosInner - cosOuter, 0.001), 0.0, 1.0);
                atten *= spotFade;
            }
            // Blocked where the light's shadow (atlas slot + 1 in .z) says so
            float localSlot = uRelightLightRange[i].z;
            if (localSlot > 0.5 && atten > 0.0) atten *= splatLocalShadowFactor(int(localSlot - 0.5), worldPos, worldNormal, false);
            lightSum += lc * atten;
        }
    }

    vec3 relit = linear * (uRelightAmbient + lightSum);
    color.rgb = rl_linearToGamma(mix(linear, relit, uRelightFade));
#endif
}
`;

export const relightingSnippetWGSL = mirrorClipSnippetWGSL + SPLAT_SHADOW_WGSL + CLOUD_SHADOW_WGSL + /* wgsl */ `
const MAX_RELIGHT_LIGHTS: i32 = 16;

uniform uRelightFade: f32;
uniform uRelightAmbient: vec3f;
uniform uRelightLightPos: array<vec3f, 16>;
uniform uRelightLightColor: array<vec3f, 16>;
// vec4 (range in .x) — array<f32>/array<vec2f> require WGSL WrappedF32/WrappedVec2F
// wrapper structs whose prelude isn't injected into 2.20's gsplat compute pass.
uniform uRelightLightRange: array<vec4f, 16>;
uniform uRelightLightDir: array<vec3f, 16>;
uniform uRelightLightCosAngles: array<vec4f, 16>; // cosInner in .x, cosOuter in .y
uniform uRelightModelMatrix: mat4x4f;

fn rl_gammaToLinear(c: vec3f) -> vec3f { return pow(c, vec3f(2.2)); }
fn rl_linearToGamma(c: vec3f) -> vec3f { return pow(max(c, vec3f(0.0)), vec3f(1.0/2.2)); }

fn applyRelighting(center: vec3f, color: ptr<function, vec4f>) {
#ifdef SHADOW_PASS
    // Faint splats (floaters) don't cast (see uSplatCasterLimits).
    if ((*color).a < uniform.uSplatCasterLimits.y) { (*color).a = 0.0; }
#else
    // Shadow-only mode (relighting off, shadows on): see the GLSL version.
    let mapShadowOnly = uniform.uSplatShadowGlobal.y < 0.5 && (uniform.uSplatShadowParams[0].x > 0.0 || uniform.uSplatShadowParams[1].x > 0.0 || uniform.uSplatLocalAtlas.w > 0.5);
    // Cloud shadows (effects/clouds): see the GLSL version.
    let cloudShadowOnly = uniform.uCloudShadowGlobal.x > 0.5 && uniform.uCloudShadowGlobal.y < 0.5;
    let shadowOnly = mapShadowOnly || cloudShadowOnly;
    if (uniform.uRelightFade <= 0.0 && !shadowOnly) { return; }

    // Transform center from model-space to world-space via the entity's world matrix.
    // Unified mode: identity (centers already world-space in work buffer).
    // Non-unified mode: entity world transform (needed when swap splats have overrides).
    let worldPos = (uniform.uRelightModelMatrix * vec4f(center, 1.0)).xyz;
    let mm = uniform.uRelightModelMatrix;
    let worldNormal = normalize(mat3x3f(mm[0].xyz, mm[1].xyz, mm[2].xyz) * rl_splatNormal);
    let shadow0 = splatShadowFactor(0, worldPos, worldNormal);
    let shadow1 = splatShadowFactor(1, worldPos, worldNormal);
    let cloudShadow = splatCloudShadowFactor(worldPos);
    if (shadowOnly) {
        // Darken the on-screen colour by the strength (see the GLSL version).
        var local = 1.0;
        for (var s: i32 = 0; s < 4; s++) { local = local * splatLocalShadowFactor(s, worldPos, worldNormal, true); }
        var maps = 1.0;
        if (mapShadowOnly) { maps = shadow0 * shadow1 * local; }
        (*color) = vec4f((*color).rgb * (maps * cloudShadow), (*color).a);
        return;
    }
    let linear = rl_gammaToLinear((*color).rgb);

    var lightSum = vec3f(0.0);
    for (var i: i32 = 0; i < MAX_RELIGHT_LIGHTS; i++) {
        let lc = uniform.uRelightLightColor[i];
        if (lc.r + lc.g + lc.b <= 0.0) { continue; }
        let range = uniform.uRelightLightRange[i].x;
        if (range <= 0.0) {
            // Directional / hemispheric: uniform contribution, removed where the light's
            // shadow map says something blocks it (.y = shadow slot + 1, 0 = no shadow)
            let slot = uniform.uRelightLightRange[i].y;
            var visible = 1.0;
            if (slot > 1.5) { visible = shadow1; } else if (slot > 0.5) { visible = shadow0; }
            // .w = 1: the light the clouds take as their sun, so their shadows remove it
            if (uniform.uRelightLightRange[i].w > 0.5) { visible = visible * cloudShadow; }
            lightSum += lc * visible;
        } else {
            // Point / spot: inverse-square falloff
            let dist = length(uniform.uRelightLightPos[i] - worldPos);
            let safeDist = max(dist, 0.5);  // Prevent singularity near light
            var atten = 1.0 / (safeDist * safeDist);
            // Soft range cutoff so light doesn't bleed infinitely
            let rangeFade = clamp(1.0 - dist / range, 0.0, 1.0);
            atten *= rangeFade;
            // Spotlight cone attenuation (point lights have dir=vec3(0), skipped)
            let lightDir = uniform.uRelightLightDir[i];
            if (dot(lightDir, lightDir) > 0.0) {
                let toSplat = normalize(worldPos - uniform.uRelightLightPos[i]);
                let cosAngle = dot(toSplat, lightDir);
                let cosOuter = uniform.uRelightLightCosAngles[i].y;
                let cosInner = uniform.uRelightLightCosAngles[i].x;
                let spotFade = clamp((cosAngle - cosOuter) / max(cosInner - cosOuter, 0.001), 0.0, 1.0);
                atten *= spotFade;
            }
            // Blocked where the light's shadow (atlas slot + 1 in .z) says so
            let localSlot = uniform.uRelightLightRange[i].z;
            if (localSlot > 0.5 && atten > 0.0) { atten *= splatLocalShadowFactor(i32(localSlot - 0.5), worldPos, worldNormal, false); }
            lightSum += lc * atten;
        }
    }

    let relit = linear * (uniform.uRelightAmbient + lightSum);
    (*color) = vec4f(rl_linearToGamma(mix(linear, relit, uniform.uRelightFade)), (*color).a);
#endif
}
`;

// ---- Standalone shader (used when relighting runs alone, no reveal) ----

const shaderGLSL = relightingSnippetGLSL + /* glsl */ `
void modifySplatCenter(inout vec3 center) {
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
}

void modifySplatColor(vec3 center, inout vec4 color) {
    applyRelighting(center, color);
}
`;

const shaderWGSL = relightingSnippetWGSL + /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    applyRelighting(center, color);
}
`;

// Cache for the script class
let _GsplatRelightingClass: typeof pc.ScriptType | null = null;

type ScriptTypeWithPrototype<T extends pc.ScriptType> = typeof pc.ScriptType & {
    prototype: T;
};

/** Max number of lights supported */
const MAX_LIGHTS = 16;

/** One light as the relighting shader sees it. */
export interface RelightLightInput {
    position: pc.Vec3;
    color: pc.Color;
    intensity: number;
    range: number;
    direction?: { x: number; y: number; z: number };
    cosInnerAngle?: number;
    cosOuterAngle?: number;
    /** Directional lights: which splat shadow map (0 / 1) blocks this light, -1 / undefined = none. */
    shadowSlot?: number;
    /** Spot / point lights: which atlas shadow slot (0-3) blocks this light, -1 / undefined = none. */
    localShadowSlot?: number;
    /** Directional lights: the clouds' sun (cloud shadows remove its light). */
    cloudSun?: boolean;
}

interface GsplatRelightingScript extends pc.ScriptType {
    material: pc.Material | null;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    _shaderManagedExternally: boolean;
    _isUnified: boolean;
    // Reusable flat arrays for uniforms
    _ambientArray: [number, number, number];
    _posArray: Float32Array;
    _colorArray: Float32Array;
    _rangeArray: Float32Array;
    _dirArray: Float32Array;
    _cosAnglesArray: Float32Array;
    // Public API
    relightFade: number;
    ambientR: number;
    ambientG: number;
    ambientB: number;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
    _applyShaders: () => void;
    _applyShaderToMaterial: (material: pc.Material) => void;
    _removeShaders: () => void;
    _discoverMaterial: () => void;
    _setUniform: (name: string, value: number | number[] | Float32Array) => void;
    setRelightFade: (fade: number) => void;
    setAmbientColor: (hex: string) => void;
    updateLights: (lights: Array<RelightLightInput>) => void;
}

/**
 * Gets or creates the GsplatRelighting script class.
 * Must be called after PlayCanvas app is initialized.
 */
export function getGsplatRelightingClass(): typeof pc.ScriptType {
    if (_GsplatRelightingClass) {
        return _GsplatRelightingClass;
    }

    const GsplatRelighting = pc.createScript('gsplatRelighting') as ScriptTypeWithPrototype<GsplatRelightingScript>;

    Object.assign(GsplatRelighting.prototype, {
        material: null as pc.Material | null,
        _effectInitialized: false,
        _shadersNeedApplication: false,
        _shaderManagedExternally: false,
        _isUnified: false,
        // Pre-allocated arrays
        _ambientArray: [1, 1, 1] as [number, number, number],
        _posArray: null as Float32Array | null,
        _colorArray: null as Float32Array | null,
        _rangeArray: null as Float32Array | null,
        _dirArray: null as Float32Array | null,
        _cosAnglesArray: null as Float32Array | null,
        // Effect properties
        relightFade: 1.0,
        ambientR: 1.0,
        ambientG: 1.0,
        ambientB: 1.0,

        initialize(this: GsplatRelightingScript) {
            this.material = null;
            this._effectInitialized = false;
            this._shadersNeedApplication = false;
            this._isUnified = !!(this.entity.gsplat as any)?.unified;
            this._ambientArray = [1, 1, 1];
            this._posArray = new Float32Array(MAX_LIGHTS * 3);
            this._colorArray = new Float32Array(MAX_LIGHTS * 3);
            this._rangeArray = new Float32Array(MAX_LIGHTS * 4); // vec4: range in .x
            this._dirArray = new Float32Array(MAX_LIGHTS * 3);
            this._cosAnglesArray = new Float32Array(MAX_LIGHTS * 4); // vec4: cosInner .x, cosOuter .y

            this.on('enable', () => {
                if (this._shaderManagedExternally) {
                    this._discoverMaterial();
                    return;
                }
                if (this._effectInitialized) {
                    this._applyShaders();
                } else {
                    this._shadersNeedApplication = true;
                }
            });

            this.on('disable', () => {
                if (!this._shaderManagedExternally) {
                    this._removeShaders();
                }
            });

            if (!this.entity.gsplat) {
                return;
            }

            this._effectInitialized = true;
            if (this.enabled) {
                if (this._shaderManagedExternally) {
                    this._discoverMaterial();
                } else {
                    this._applyShaders();
                }
            }
        },

        update(this: GsplatRelightingScript, _dt: number) {
            // Try to complete initialization
            if (!this._effectInitialized) {
                if (this.entity.gsplat) {
                    this._effectInitialized = true;
                    this._isUnified = !!(this.entity.gsplat as any)?.unified;
                    if (this.enabled && !this._shaderManagedExternally && this._shadersNeedApplication) {
                        this._applyShaders();
                        this._shadersNeedApplication = false;
                    } else if (this.enabled && this._shaderManagedExternally && !this.material) {
                        this._discoverMaterial();
                    }
                }
                return;
            }

            if (!this._shaderManagedExternally && this._shadersNeedApplication) {
                this._applyShaders();
                this._shadersNeedApplication = false;
            }

            if (!this.material) {
                // Material wasn't ready during initial _applyShaders (e.g. unified
                // gsplat template material created lazily). Retry until it appears.
                if (!this._shaderManagedExternally) {
                    this._applyShaders();
                } else {
                    this._discoverMaterial();
                }
                if (!this.material) return;
            }

            // Push ambient and fade every frame (cheap)
            this._ambientArray[0] = this.ambientR;
            this._ambientArray[1] = this.ambientG;
            this._ambientArray[2] = this.ambientB;
            this._setUniform('uRelightAmbient', this._ambientArray);
            this._setUniform('uRelightFade', this.relightFade);
            // Upload entity world transform so the shader can convert model-space
            // centers to world-space. For unified gsplat, centers are already
            // world-space so we use identity (the entity transform is baked into
            // the work buffer). For non-unified, we need the actual world matrix.
            if (this._isUnified) {
                this._setUniform('uRelightModelMatrix', pc.Mat4.IDENTITY.data as unknown as Float32Array);
            } else {
                this._setUniform('uRelightModelMatrix', this.entity.getWorldTransform().data as unknown as Float32Array);
            }
            // The shader declares the splat shadow-map slots; keep something valid bound to them
            // until the shadow system binds a light's map (see effects/shadows).
            ensureShadowMapBindings(this.material, this.app.graphicsDevice);
            // Must call material.update() for gsplat materials to pick up
            // parameter changes. During reveal, the reveal script does this,
            // but after reveal completes we need to do it ourselves.
            this.material.update();
        },

        setRelightFade(this: GsplatRelightingScript, fade: number) {
            this.relightFade = Math.max(0, Math.min(1, fade));
        },

        setAmbientColor(this: GsplatRelightingScript, hex: string) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            if (result) {
                this.ambientR = parseInt(result[1], 16) / 255;
                this.ambientG = parseInt(result[2], 16) / 255;
                this.ambientB = parseInt(result[3], 16) / 255;
            }
        },

        updateLights(this: GsplatRelightingScript, lights: Array<RelightLightInput>) {
            const count = Math.min(lights.length, MAX_LIGHTS);
            for (let i = 0; i < count; i++) {
                const l = lights[i];
                this._posArray![i * 3] = l.position.x;
                this._posArray![i * 3 + 1] = l.position.y;
                this._posArray![i * 3 + 2] = l.position.z;
                this._colorArray![i * 3] = l.color.r * l.intensity;
                this._colorArray![i * 3 + 1] = l.color.g * l.intensity;
                this._colorArray![i * 3 + 2] = l.color.b * l.intensity;
                this._rangeArray![i * 4] = l.range;
                // Shadow slot + 1 for directional lights whose shadow map the splats sample (0 = none)
                this._rangeArray![i * 4 + 1] = l.shadowSlot !== undefined && l.shadowSlot >= 0 ? l.shadowSlot + 1 : 0;
                // Spot / point lights: atlas shadow slot + 1 (0 = none)
                this._rangeArray![i * 4 + 2] = l.localShadowSlot !== undefined && l.localShadowSlot >= 0 ? l.localShadowSlot + 1 : 0;
                // The clouds' sun: cloud shadows remove this light (1), others ignore them (0)
                this._rangeArray![i * 4 + 3] = l.cloudSun ? 1 : 0;
                // Direction (non-zero for spotlights, zero for point lights)
                if (l.direction) {
                    this._dirArray![i * 3] = l.direction.x;
                    this._dirArray![i * 3 + 1] = l.direction.y;
                    this._dirArray![i * 3 + 2] = l.direction.z;
                } else {
                    this._dirArray![i * 3] = 0;
                    this._dirArray![i * 3 + 1] = 0;
                    this._dirArray![i * 3 + 2] = 0;
                }
                // Cone angles (cosInner, cosOuter)
                this._cosAnglesArray![i * 4] = l.cosInnerAngle ?? 1;
                this._cosAnglesArray![i * 4 + 1] = l.cosOuterAngle ?? 1;
            }
            // Zero out unused slots so shader skips them
            for (let i = count; i < MAX_LIGHTS; i++) {
                this._colorArray![i * 3] = 0;
                this._colorArray![i * 3 + 1] = 0;
                this._colorArray![i * 3 + 2] = 0;
                this._dirArray![i * 3] = 0;
                this._dirArray![i * 3 + 1] = 0;
                this._dirArray![i * 3 + 2] = 0;
            }
            // PlayCanvas requires [0] suffix for array uniform names
            this._setUniform('uRelightLightPos[0]', this._posArray!);
            this._setUniform('uRelightLightColor[0]', this._colorArray!);
            this._setUniform('uRelightLightRange[0]', this._rangeArray!);
            this._setUniform('uRelightLightDir[0]', this._dirArray!);
            this._setUniform('uRelightLightCosAngles[0]', this._cosAnglesArray!);
        },

        getShaderGLSL(): string {
            return shaderGLSL;
        },

        getShaderWGSL(): string {
            return shaderWGSL;
        },

        /** Discover the gsplat material without applying shaders (for external management) */
        _discoverMaterial(this: GsplatRelightingScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                this.material = (this.app.scene as any).gsplat?.material ?? null;
            } else {
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                } else {
                    gsplat.once('load', () => {
                        this.material = gsplat.material ?? null;
                    });
                }
            }
        },

        _applyShaders(this: GsplatRelightingScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                // Unified mode: template material
                this.material = (this.app.scene as any).gsplat?.material ?? null;
                if (!this.material) {
                    console.warn('[Relighting] Unified gsplat template material not available');
                    return;
                }
                this._applyShaderToMaterial(this.material);
            } else {
                // Non-unified mode
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                    this._applyShaderToMaterial(mat);
                } else {
                    gsplat.once('load', () => {
                        this.material = gsplat.material ?? null;
                        if (this.material) {
                            this._applyShaderToMaterial(this.material);
                        }
                    });
                }
            }
        },

        _applyShaderToMaterial(this: GsplatRelightingScript, material: pc.Material) {
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            const shader = lang === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL();

            material.getShaderChunks(lang).set('gsplatModifyVS', shader);
            ensureShadowMapBindings(material, device);
            material.update();
        },

        _removeShaders(this: GsplatRelightingScript) {
            if (!this.material) return;

            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';

            this.material.getShaderChunks(lang).delete('gsplatModifyVS');
            this.material.update();
            this.material = null;
        },

        _setUniform(this: GsplatRelightingScript, name: string, value: number | number[] | Float32Array) {
            this.material?.setParameter(name, value);
        },

        destroy(this: GsplatRelightingScript) {
            if (!this._shaderManagedExternally) {
                this._removeShaders();
            }
        }
    });

    _GsplatRelightingClass = GsplatRelighting;
    return GsplatRelighting;
}

// For backwards compatibility
export const GsplatRelighting = {
    get class() {
        return getGsplatRelightingClass();
    }
};
